import './index.css'
import cn from 'clsx'

import React, { Component } from 'react'
import Tooltip from 'components/ui/tooltip'
import Check from './check'
type Props = {
  className?: string
  hasCheck?: boolean
  children?: React.ReactNode
  inputClassName?: string
  labelClassName?: string
  label?: React.ReactNode
  value?: any
  disabled?: boolean
  checked: boolean
  onBlur?: (...args: Array<any>) => any
  onFocus?: (...args: Array<any>) => any
  onChange?: (...args: Array<any>) => any
  wrapText?: boolean
  tooltip?: string
}
/**
 * @deprecated Use `RadioGroup` from `components/ds` instead.
 * @see {@link components/ds/RadioGroup}
 * @migration
 * - Replace legacy RadioButton/RadioGroup with DS RadioGroup compound component:
 *   ```tsx
 *   // Legacy
 *   <RadioGroup value={value} onChange={onChange}>
 *     <RadioButton value="1" label="Option 1" />
 *     <RadioButton value="2" label="Option 2" />
 *   </RadioGroup>
 *
 *   // DS alternative
 *   <RadioGroup.Root value={value} onValueChange={onChange}>
 *     <RadioGroup.Item value="1">Option 1</RadioGroup.Item>
 *     <RadioGroup.Item value="2">Option 2</RadioGroup.Item>
 *   </RadioGroup.Root>
 *   ```
 * - Prop mapping:
 *   - checked → Managed internally by RadioGroup.Root via `value`
 *   - disabled → disabled (same)
 *   - value → value (same)
 *   - onChange → Use onValueChange on RadioGroup.Root
 *   - label → Use children or compose with `<Text>` inside RadioGroup.Item
 *   - tooltip → Wrap RadioGroup.Item with DS Tooltip
 * - hasCheck/wrapText have no DS equivalent; styling is handled by Radix themes
 *
 * A radio button component with customizable appearance and optional tooltip support
 * @param className - Additional CSS classes for the root element
 * @param hasCheck - Whether to show the check/circle indicator (default: true)
 * @param children - Additional content to render inside the radio button
 * @param inputClassName - CSS classes for the input element
 * @param labelClassName - CSS classes for the label element
 * @param label - Label text or content to display next to the radio button
 * @param value - Value associated with this radio button
 * @param disabled - Whether the radio button is disabled (default: false)
 * @param checked - Whether the radio button is currently selected (required)
 * @param onBlur - Callback fired when the radio button loses focus
 * @param onFocus - Callback fired when the radio button gains focus
 * @param onChange - Callback fired when the radio button is clicked/changed
 * @param wrapText - Whether to wrap text in the label (default: false)
 * @param tooltip - Optional tooltip text to display on hover
 * @returns Radio button component with optional tooltip wrapper
 */
export default class RadioButton extends Component<Props> {
  static defaultProps: {
    checked: boolean
    disabled: boolean
    hasCheck: boolean
    wrapText: boolean
  } = {
    checked: false,
    hasCheck: true,
    disabled: false,
    wrapText: false,
  }
  onClick: (event: React.MouseEvent<EventTarget>) => void = (
    event: React.MouseEvent<any>,
  ) => {
    const { disabled, checked, onChange } = this.props
    if (!disabled && !checked && !!onChange) onChange(event)
  }

  render(): React.ReactElement<'label'> | React.ReactNode {
    const {
      className,
      inputClassName,
      labelClassName,
      hasCheck,
      children,
      label,
      checked,
      disabled,
      wrapText,
      tooltip,
    } = this.props
    const rootClass = cn(
      disabled ? 'RadioButton_Disabled' : 'RadioButton_Enabled',
      {
        'RadioButton--WrapText': wrapText,
      },
      className,
    )
    const labelClass = cn('RadioButton_Label', labelClassName, {
      checked,
      'RadioButton_Label--WrapText': wrapText,
    })
    const inputClass = cn('RadioButton_Input', inputClassName)
    const control = (
      <label className={rootClass}>
        <input
          className={inputClass}
          readOnly
          type="radio"
          onClick={this.onClick}
        />
        {hasCheck && (
          <Check
            {...{
              checked,
              disabled,
              wrapText,
            }}
          />
        )}
        {label && <span className={labelClass}>{label}</span>}
        {children}
      </label>
    )

    if (tooltip) {
      return <Tooltip content={tooltip}>{control}</Tooltip>
    }

    return control
  }
}
